import { Entity, SystemInterface } from '@goodboydigital/odie';
import { Container } from '@pixi/display';

import { Settings } from '../../AppConfig';
import { UserPlugin } from '../../plugins/UserPlugin';
import { StrUtils } from '../../utils/StrUtils';
import { CharacterData, CharacterEntity } from '../entities/CharacterEntity';
import { ChassisData, ChassisEntity } from '../entities/ChassisEntity';
import { DecoEntity } from '../entities/DecoEntity';
import { TrailData } from '../entities/TrailEntity';
import { VehicleData } from '../entities/VehicleEntity';
import { WheelData, WheelEntity } from '../entities/WheelEntity';
import { Game } from '../Game';
import { PhysicsDrawSystem } from '../physics/PhysicsDrawSystem';

/**
 * Change vehicle parts, character and colour, also save selected setup
 * List of available characters, wheels and chassis comes from loaded data,
 * means it will have as many wheels as there are `wheel-id.json` files in assets folder.
 * Same for characters with `character-id.json` and chassis with `chassis-id.json` files.
 */
export class GarageSystem implements SystemInterface
{
    public game: Game;

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.setup();
    }

    /**
     * Reset vehicle to user's saved vehicle settings
     * I user data has been reset, vehicle will be updated to initial state,
     * which randomise some parts
     */
    public resetVehicle(): void
    {
        this.setup();
        this.updateVehicle();
    }

    /** Setup user vehicle parts */
    public setup(): void
    {
        this.user.data.vehicle = this.normaliseVehicleData(this.user.data.vehicle);

        // Fully randomise vehicle parts and name for recording
        if (Settings.rec || Settings.randomGear) this.randomiseVehicle(false);

        this.user.save();
    }

    /**
     * Randomise all vehicles parts: chassis, character, wheels, trails, decoration, flag and nickname
     * @param autoUpdateEntities - Update current vehicle visuals
     */
    public randomiseVehicle(autoUpdateEntities = true, tier = Infinity): void
    {
        // Fully randomise vehicle parts and name for recording
        this.user.data.vehicle.character = this.game.app.data.getRandomCharacter(tier);
        this.user.data.vehicle.chassis = this.game.app.data.getRandomChassis(tier);
        this.user.data.vehicle.wheelLeft = this.game.app.data.getRandomWheel(tier);
        this.user.data.vehicle.wheelRight = this.game.app.data.getRandomWheel(tier);
        this.user.data.vehicle.extra = this.game.app.data.getRandomDeco(tier);
        this.user.data.vehicle.trailLeft = this.game.app.data.getRandomTrail(tier);
        this.user.data.vehicle.trailRight = this.user.data.vehicle.trailLeft;
        this.user.data.vehicle.colorChassis = this.game.app.data.getRandomColor(tier);
        this.user.data.vehicle.colorWheelLeft = this.game.app.data.getRandomColor(tier);
        this.user.data.vehicle.colorWheelRight = this.game.app.data.getRandomColor(tier);
        this.user.data.vehicle.nickname = StrUtils.randomNickname();
        this.user.data.vehicle.flag = this.game.app.data.getRandomFlag();

        this.user.save();

        if (autoUpdateEntities) this.updateVehicle();
    }

    /**
     * Read data and replace undefined/non-existend info by random unlocked parts.
     * Useful to prevent removed content from breaking the game.
     * @param data - Vehicle data to be normalised
     * @returns
     */
    public normaliseVehicleData(data: Partial<VehicleData>): VehicleData
    {
        const vehicleData = { ...data } as VehicleData;

        // REGULAR PARTS
        if (!this.game.app.data.isCharacterAvailable(vehicleData.character))
        {
            vehicleData.character = this.game.app.data.getRandomUnlockedCharacter();
        }
        if (!this.game.app.data.isWheelAvailable(vehicleData.wheelLeft))
        {
            vehicleData.wheelLeft = this.game.app.data.getRandomUnlockedWheel();
        }
        if (!this.game.app.data.isWheelAvailable(vehicleData.wheelRight))
        {
            vehicleData.wheelRight = vehicleData.wheelLeft;
        }
        if (!this.game.app.data.isChassisAvailable(vehicleData.chassis))
        {
            vehicleData.chassis = this.game.app.data.getRandomUnlockedChassis();
        }

        // COLORS
        if (!this.game.app.data.isColorAvailable(vehicleData.colorChassis))
        {
            vehicleData.colorChassis = this.game.app.data.getRandomUnlockedColor();
        }
        if (!this.game.app.data.isColorAvailable(vehicleData.colorWheelLeft))
        {
            vehicleData.colorWheelLeft = this.game.app.data.getRandomUnlockedColor();
        }
        if (!this.game.app.data.isColorAvailable(vehicleData.colorWheelRight))
        {
            vehicleData.colorWheelRight = this.game.app.data.getRandomUnlockedColor();
        }

        // TRAILS & EXTRAS
        if (!vehicleData.nickname)
        {
            vehicleData.nickname = StrUtils.randomNickname();
        }
        if (!this.game.app.data.isFlagAvailable(vehicleData.flag))
        {
            vehicleData.flag = this.game.app.data.getRandomFlag();
        }
        if (!this.game.app.data.isTrailAvailable(vehicleData.trailLeft))
        {
            vehicleData.trailLeft = this.game.app.data.getRandomUnlockedTrail();
        }
        if (!this.game.app.data.isTrailAvailable(vehicleData.trailRight))
        {
            vehicleData.trailRight = vehicleData.trailLeft;
        }
        if (!this.game.app.data.isDecoAvailable(vehicleData.extra))
        {
            vehicleData.extra = this.game.app.data.getRandomUnlockedDeco();
        }

        return vehicleData;
    }

    /** A unencoded hash with a combination of values we don't want to see repeated in the same race */
    public getVehicleDataHash(data: VehicleData): string
    {
        return [data.nickname, data.flag].join('|');
    }

    /** Refesh vehicle with curent selected parts */
    public updateVehicle(): void
    {
        if (Settings.spineDebug)
        {
            this.game.spineDebug.updateVehicles();

            return;
        }

        this.game.vehicle.build(this.user.data.vehicle);
        this.updateColor();
        
        const drawSystem = this.game.getSystem(PhysicsDrawSystem as any) as PhysicsDrawSystem;

        if (drawSystem) drawSystem.cleanup();
    }

    /** Refesh vehicle with curent selected colour */
    public updateColor(): void
    {
        if (Settings.spineDebug)
        {
            this.game.spineDebug.updateVehicles();

            return;
        }

        this.game.vehicle.updateColor(
            this.user.data.vehicle.colorChassis,
            this.user.data.vehicle.colorWheelLeft,
            this.user.data.vehicle.colorWheelRight,
        );
    }

    // ===== SET PARTS BY ID ==========================================================================================

    public saveVehicleSelection(): void
    {
        this.user.save();
    }

    public setWheelLeft(id: string): void
    {
        if (!this.game.app.data.isWheelAvailable(id)) throw new Error(`[GarageSystem] Wheel not available: ${id}`);
        this.user.data.vehicle.wheelLeft = id;
        this.updateVehicle();
    }

    public setWheelRight(id: string): void
    {
        if (!this.game.app.data.isWheelAvailable(id)) throw new Error(`[GarageSystem] Wheel not available: ${id}`);
        this.user.data.vehicle.wheelRight = id;
        this.updateVehicle();
    }

    public setChassis(id: string): void
    {
        if (!this.game.app.data.isChassisAvailable(id)) throw new Error(`[GarageSystem] Chassis not available: ${id}`);
        this.user.data.vehicle.chassis = id;
        this.updateVehicle();
        
    }

    public setCharacter(id: string, fullRebuild = false): void
    {
        if (!this.game.app.data.isCharacterAvailable(id)) throw new Error(`[GarageSystem] Character not available: ${id}`);
        this.user.data.vehicle.character = id;
        if (fullRebuild)
        {
            this.updateVehicle();
        }
        else
        {
            const vehicle = this.game.vehicle;
            const data = this.game.app.assets.getData<CharacterData>(`character-${id}`);
            
            vehicle.buildCharacter({ ...data, ghost: false }, vehicle.chassis.data?.type || 'car');
            //vehicle.character.build({ ...data, ghost: false }, vehicle.chassis.data?.type || 'car');
        }
    }

    public setTrailLeft(id: string, fullRebuild = false): void
    {
        if (!this.game.app.data.isTrailAvailable(id)) throw new Error(`[GarageSystem] Trail id not available: ${id}`);

        this.user.data.vehicle.trailLeft = id;
        if (fullRebuild)
        {
            this.updateVehicle();
        }
        else
        {
            const vehicle = this.game.vehicle;
            const data = this.game.app.assets.getData<TrailData>(`trail-${id}`);

            vehicle.trailLeft.build({ ...data });
        }
    }

    public setTrailRight(id: string, fullRebuild = false): void
    {
        if (!this.game.app.data.isTrailAvailable(id)) throw new Error(`[GarageSystem] Trail id not available: ${id}`);

        this.user.data.vehicle.trailRight = id;

        if (Settings.spineDebug)
        {
            this.game.spineDebug.updateVehicles();

            return;
        }

        if (fullRebuild)
        {
            this.updateVehicle();
        }
        else
        {
            const vehicle = this.game.vehicle;
            const data = this.game.app.assets.getData<TrailData>(`trail-${id}`);

            vehicle.trailRight.build({ ...data });
        }
    }

    public setTrail(id: string, fullRebuild = false): void
    {
        this.setTrailLeft(id, fullRebuild);
        this.setTrailRight(id, fullRebuild);
    }

    public setExtra(id: string, fullRebuild = false): void
    {
        if (!this.game.app.data.isDecoAvailable(id)) throw new Error(`[GarageSystem] Extra id not available: ${id}`);

        this.user.data.vehicle.extra = id;

        if (Settings.spineDebug)
        {
            this.game.spineDebug.updateVehicles();

            return;
        }

        if (fullRebuild)
        {
            this.updateVehicle();
        }
        else
        {
            DecoEntity.mountInVehicle(id, this.game.vehicle);
        }
    }

    public setColor(color: string): void
    {
        if (!this.game.app.data.isColorAvailable(color)) throw new Error(`[GarageSystem] Color not available: ${color}`);
        this.user.data.vehicle.colorChassis = color;
        this.user.data.vehicle.colorWheelLeft = color;
        this.user.data.vehicle.colorWheelRight = color;
        this.updateColor();
    }

    public setColorChassis(color: string): void
    {
        if (!this.game.app.data.isColorAvailable(color)) throw new Error(`[GarageSystem] Color not available: ${color}`);
        this.user.data.vehicle.colorChassis = color;
        this.updateColor();
    }

    public setColorWheelLeft(color: string): void
    {
        if (!this.game.app.data.isColorAvailable(color)) throw new Error(`[GarageSystem] Color not available: ${color}`);
        this.user.data.vehicle.colorWheelLeft = color;
        this.updateColor();
    }

    public setColorWheelRight(color: string): void
    {
        if (!this.game.app.data.isColorAvailable(color)) throw new Error(`[GarageSystem] Color not available: ${color}`);
        this.user.data.vehicle.colorWheelRight = color;
        this.updateColor();
    }

    public setRandomNickname(): void
    {
        this.user.data.vehicle.nickname = StrUtils.randomNickname();
        if(this.game.vehicle.tag){
            this.game.vehicle.tag.setNickname(this.user.data.vehicle.nickname);
        }
        this.updateColor();
        this.user.save();
    }

    // ===== USEFUL GETTERS ===========================================================================================

    public get user(): UserPlugin
    {
        return this.game.app.user;
    }

    public get selectedCharacter(): string
    {
        return this.user.data.vehicle.character;
    }

    public get selectedChassis(): string
    {
        return this.user.data.vehicle.chassis;
    }

    public get selectedWheelLeft(): string
    {
        return this.user.data.vehicle.wheelLeft;
    }

    public get selectedWheelRight(): string
    {
        return this.user.data.vehicle.wheelRight;
    }

    public get selectedColorChassis(): string
    {
        return this.user.data.vehicle.colorChassis;
    }

    public get selectedColorWheelLeft(): string
    {
        return this.user.data.vehicle.colorWheelLeft;
    }

    public get selectedColorWheelRight(): string
    {
        return this.user.data.vehicle.colorWheelRight;
    }

    public get selectedColor(): string
    {
        return this.user.data.vehicle.colorChassis;
    }

    public get selectedTrail(): string
    {
        return this.user.data.vehicle.trailLeft;
    }

    public get selectedTrailLeft(): string
    {
        return this.user.data.vehicle.trailLeft;
    }

    public get selectedTrailRight(): string
    {
        return this.user.data.vehicle.trailRight;
    }

    public get selectedExtra(): string
    {
        return this.user.data.vehicle.extra;
    }

    public get selectedFlag(): string
    {
        return this.user.data.vehicle.flag;
    }

    public get chassis(): string[]
    {
        return this.game.app.data.getAvailableChassis();
    }

    public get characters(): string[]
    {
        return this.game.app.data.getAvailableCharacters();
    }

    public get wheels(): string[]
    {
        return this.game.app.data.getAvailableWheels();
    }

    public get trails(): string[]
    {
        return this.game.app.data.getAvailableTrails();
    }

    public get extras(): string[]
    {
        return this.game.app.data.getAvailableDecos();
    }

    public get colors(): string[]
    {
        return this.game.app.data.getAvailableColors();
    }

    public get flags(): string[]
    {
        return this.game.app.data.getAvailableFlags();
    }

    /**
     * Mount a vehicle based on part ids provided.
     * To create individual parts:
     * ```
     * wheel = getPlainSprite({wheelLeft: 'monster', color: '#FFCC00'})
     * chassis = getPlainSprite({chassis: 'hotdog', color: '#FFCC00'})
     * character = getPlainSprite({character: 'finn', color: '#FFCC00'})
     * ```
     * To get plain sprite from current selected parts:
     * ```
     * all = garage.getPlainSprite({
     *   chassis: garage.selectedChassis,
     *   wheelLeft: garage.selectedWheelLeft,
     *   wheelRight: garage.selectedWheelRight,
     *   character: garage.selectedCharacter,
     *   color: garage.selectedColor,
     * })
     * ```
     * */
    public getPlainSprite({
        chassis = '',
        wheelLeft = '',
        wheelRight = '',
        character = '', color = '',
        wheelLeftColor = '',
        wheelRightColor = '' }
    = {}): Container
    {
        const container = new Container();

        // Sprites that might be created or not, according to provided params
        let wheelLeftSprite: Container | null = null;
        let wheelRightSprite: Container | null = null;
        let chassisSprite: Container | null = null;
        let characterSprite: Container | null = null;
        let chassisType = 'car';

        // Raw data to be used for creating the sprites
        const wheelLeftData = wheelLeft ? this.game.app.assets.getData<WheelData>(`wheel-${wheelLeft}`) : null;
        const wheelRightData = wheelRight ? this.game.app.assets.getData<WheelData>(`wheel-${wheelRight}`) : null;
        const chassisData = chassis ? this.game.app.assets.getData<ChassisData>(`chassis-${chassis}`) : null;
        const characterData = character ? this.game.app.assets.getData<CharacterData>(`character-${character}`) : null;

        if (chassisData) chassisType = chassisData.type || 'car';

        // Mount left wheel
        if (wheelLeft && wheelLeftData)
        {
            wheelLeftSprite = new Container();
            WheelEntity.mountSprite(wheelLeftSprite, wheelLeftData, wheelLeftColor || color);
        }

        // Mount right wheel
        if (wheelRight && wheelRightData)
        {
            wheelRightSprite = new Container();
            WheelEntity.mountSprite(wheelRightSprite, wheelRightData, wheelRightColor || color);
        }

        // Set a default position for both wheels, if they are present
        if (wheelLeftSprite && wheelRightSprite)
        {
            wheelLeftSprite.position.set(-50, 30);
            wheelRightSprite.position.set(50, 30);
        }

        // Mount character
        if (character && characterData)
        {
            characterSprite = new Container();
            CharacterEntity.mountSprite(characterSprite, characterData, chassisType);
        }

        // Mount chassis
        if (chassis && chassisData)
        {
            chassisSprite = new Container();
            ChassisEntity.mountSprite(chassisSprite, chassisData, color);

            // All other parts are relative to chassis position
            if (wheelLeftSprite) wheelLeftSprite.position.set(chassisData.wheelLeftX, chassisData.wheelLeftY);
            if (wheelRightSprite) wheelRightSprite.position.set(chassisData.wheelRightX, chassisData.wheelRightY);
            if (characterSprite && characterData)
            {
                characterSprite.x = chassisData.seatX - characterData.seatX;
                characterSprite.y = chassisData.seatY - characterData.seatY;
            }
        }

        // Add created sprites to the container
        if (characterSprite) container.addChild(characterSprite);
        if (chassisSprite) container.addChild(chassisSprite);
        if (wheelLeftSprite) container.addChild(wheelLeftSprite);
        if (wheelRightSprite) container.addChild(wheelRightSprite);
        if (characterSprite && chassisType === 'bike') container.addChild(characterSprite);

        return container;
    }

    public update(): void
    {
        // Only to make a valid SystemInterface
    }

    public getProfile(): Record<string, any>
    {
        return {
            character: this.selectedCharacter,
            chassis: this.selectedChassis,
            wheels1: this.selectedWheelLeft,
            wheels2: this.selectedWheelRight,
            trails: this.selectedTrail,
            colorChassis: this.selectedColorChassis,
            colorWheel1: this.selectedColorWheelLeft,
            colorWheel2: this.selectedColorWheelRight,
        };
    }
}
